import { watch } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { workspacesEnabled } from '@/composables/useWorkspaces'
import { i18n } from '@/i18n'
import type { MessageKey } from '@/i18n/messages'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    isCallback?: boolean
    /** ADR-010 D5: catalog key for the route title; a router hook sets `document.title` from it. */
    titleKey?: MessageKey
  }
}

const workspaceRoutes: RouteRecordRaw[] = workspacesEnabled
  ? [
      {
        path: '/workspaces',
        name: 'workspaces',
        component: () => import('../views/WorkspaceListView.vue'),
        meta: { requiresAuth: true, titleKey: 'routes.workspaces' }
      },
      {
        path: '/workspaces/:id',
        name: 'workspace',
        component: () => import('../views/WorkspaceView.vue'),
        meta: { requiresAuth: true, titleKey: 'routes.workspace' }
      }
    ]
  : []

const routes: RouteRecordRaw[] = [
  ...workspaceRoutes,
  {
    path: '/',
    name: 'landing',
    component: () => import('../views/LandingView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/activity',
    redirect: '/home'
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('../views/HomeView.vue'),
    meta: { requiresAuth: true, titleKey: 'feed.title' }
  },
  {
    path: '/library',
    name: 'library',
    component: () => import('../views/LibraryView.vue'),
    meta: { requiresAuth: true, titleKey: 'rooms.library' }
  },
  {
    path: '/library/sso',
    name: 'library-sso',
    component: () => import('../views/LibrarySsoView.vue'),
    // Auth is handled inside the view — the guard must not redirect away and
    // lose the sso/sig query params before the view can preserve them.
    meta: { requiresAuth: false, titleKey: 'sso.signingIn' }
  },
  {
    path: '/library/:section/:thread',
    name: 'library-discussion',
    component: () => import('../views/LibraryView.vue'),
    meta: { requiresAuth: true, titleKey: 'rooms.library' }
  },
  {
    path: '/library/:section',
    name: 'library-section',
    component: () => import('../views/LibraryView.vue'),
    meta: { requiresAuth: true, titleKey: 'rooms.library' }
  },
  {
    path: '/magazine',
    name: 'magazine',
    component: () => import('../views/MagazineView.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.magazine' }
  },
  {
    path: '/atlas',
    name: 'atlas',
    component: () => import('../views/ComingSoonView.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.atlas' }
  },
  {
    path: '/performers',
    name: 'performers',
    component: () => import('../views/ComingSoonView.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.performers' }
  },
  {
    path: '/performers/:profileId',
    name: 'performer-summary',
    component: () => import('../views/PublicPerformerView.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.performerProfile' }
  },
  {
    path: '/longform',
    name: 'longform',
    component: () => import('../views/ComingSoonView.vue'),
    meta: { requiresAuth: true, titleKey: 'routes.longform' }
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('../views/ProfileView.vue'),
    meta: { requiresAuth: true, titleKey: 'profile.title' }
  },
  {
    path: '/callback',
    name: 'callback',
    component: () => import('../views/CallbackView.vue'),
    meta: { requiresAuth: false, isCallback: true, titleKey: 'callback.signingIn' }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFoundView.vue'),
    meta: { requiresAuth: false, titleKey: 'routes.notFound' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  if (to.path === '/callback' && to.query.code && to.query.state) {
    try {
      const targetUrl = await authStore.handleRedirectCallback()
      next(targetUrl || '/home')
      return
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error('Auth callback error:', err)
      }
      next('/?error=callback_failed')
      return
    }
  }

  await authStore.initialize()

  if (to.name === 'landing' && authStore.isAuthenticated) {
    next({ name: 'home', replace: true })
    return
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth === true)
  if (requiresAuth && !authStore.isAuthenticated) {
    next({ path: '/', query: { redirect: to.fullPath } })
    return
  }

  next()
})

function applyDocumentTitle(meta: { titleKey?: MessageKey }): void {
  const { t } = i18n.global
  const brand = t('brand.name')
  document.title = meta.titleKey ? `${t(meta.titleKey)} · ${brand}` : brand
}

router.afterEach(to => {
  applyDocumentTitle(to.meta)
})

// The language can change after the first navigation (the account language arrives later).
watch(i18n.global.locale, () => {
  applyDocumentTitle(router.currentRoute.value.meta)
})

export default router
